import { useHubSpotPipelines } from "@/api/integrations/hubspot";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useIntegrationConnection } from "@/hooks/useIntegrationConnection";
import { HubspotPipelineEntity } from "@cohesive/api-entities";
import { getEntityId, Spinner } from "@cohesive/ui-components";
import { ControlProps, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { ChevronsUpDown, Handshake, Ticket, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { optionsAre } from "./testers";

interface HubSpotPipelineSearchControlProps extends Omit<ControlProps, "data"> {
  data: HubspotPipelineEntity | string | undefined;
  pipelineType: "deals" | "tickets";
}

const getPipelineIcon = (pipelineType: "deals" | "tickets") => {
  switch (pipelineType) {
    case "deals":
      return Handshake;
    case "tickets":
      return Ticket;
    default:
      return Handshake;
  }
};

const HubSpotPipelineSearchControl: React.FC<
  HubSpotPipelineSearchControlProps
> = ({ data, enabled, handleChange, path, pipelineType, label }) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const integration = useIntegrationConnection("hubspot");

  // Extract ID from data (handles both string ID and entity object)
  const selectedId =
    typeof data === "string" ? data : data ? getEntityId(data as any) : null;

  const { data: pipelinesData, isLoading: loading } = useHubSpotPipelines(
    {
      integrationConnectionId: integration?.id || "",
      type: pipelineType,
    },
    {
      enabled: Boolean(integration),
    },
  );

  // Toolbox API returns array of pipeline entities
  const pipelines = pipelinesData || [];

  const selectedPipeline = useMemo(() => {
    return pipelines.find((p) => selectedId === p.data.id);
  }, [pipelines, selectedId]);

  const Icon = getPipelineIcon(pipelineType);

  const filteredPipelines = useMemo(() => {
    if (!searchQuery) return pipelines;
    return pipelines.filter((p) =>
      p.data.label.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [pipelines, searchQuery]);

  return (
    <div>
      {label && <label className="text-sm font-medium">{label}</label>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
          >
            {selectedPipeline ? (
              <div className="flex items-center gap-2 overflow-hidden">
                <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                  <Icon className="w-4! h-4!" />
                </div>
                <span className="truncate">{selectedPipeline.data.label}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">Select pipeline...</span>
            )}
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedPipeline && (
                <div
                  role="button"
                  aria-label="Clear selection"
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleChange(path, null);
                  }}
                >
                  <X className="h-4 w-4 opacity-50 hover:opacity-100" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search pipelines..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              {loading ? (
                <div className="flex items-center justify-center p-4">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  <CommandEmpty>No pipelines found.</CommandEmpty>
                  <CommandGroup>
                    {filteredPipelines.map((pipeline) => (
                      <CommandItem
                        key={pipeline.data.id}
                        value={pipeline.data.label}
                        onSelect={() => {
                          // Return full entity object
                          handleChange(path, pipeline);
                          setOpen(false);
                        }}
                      >
                        <div className="flex items-center justify-center w-7 h-7 shrink-0 rounded-full bg-muted">
                          <Icon className="w-4! h-4!" />
                        </div>
                        <span className="truncate">{pipeline.data.label}</span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export const hubSpotPipelineDealTester = rankWith(
  5,
  optionsAre({
    control: "hubspot_pipeline",
    pipeline_type: "deals",
  }),
);

export const hubSpotPipelineTicketTester = rankWith(
  5,
  optionsAre({
    control: "hubspot_pipeline",
    pipeline_type: "tickets",
  }),
);

export default withJsonFormsControlProps((props: ControlProps) => {
  const { pipeline_type } = props.uischema.options || {};

  if (!pipeline_type) {
    return null;
  }

  return (
    <HubSpotPipelineSearchControl {...props} pipelineType={pipeline_type} />
  );
});
